import { toast } from 'sonner';

import i18n from '@/utils/i18n';
import { useGetAllContainerTypeGroupsQuery } from '../../../graphql/queries/getAllContainerTypeGroups.generated';
import { useCreateContainerTypeGroupMutation } from '../../../graphql/mutations/createContainerTypeGroup.generated';
import { useUpdateContainerTypeGroupMutation } from '../../../graphql/mutations/updateContainerTypeGroup.generated';
import { useDeleteContainerTypeGroupMutation } from '../../../graphql/mutations/deleteContainerTypeGroup.generated';
import type { ContainerTypeGroup, GroupCategory } from './groupHelpers';

const REFETCH = ['GetAllContainerTypeGroups'];

/**
 * Data layer for the redesigned Container type groups screen. All reads and
 * writes go through the colugo GraphQL admin API (same operations the legacy
 * screen used — no REST). Success/error surfaces as sonner toasts to match the
 * handoff; the list refetches after every mutation.
 *
 * NOTE: colugo's `updateContainerTypeGroup` only persists `category` when it is
 * non-null, so an existing category cannot be cleared back to "No category".
 * This mirrors the legacy behaviour and is intentionally left unchanged here.
 */
export function useContainerTypeGroupsAdmin() {
  const { data, loading, error } = useGetAllContainerTypeGroupsQuery({
    fetchPolicy: 'no-cache',
  });

  const onMutationError = (e: { message: string }) =>
    toast.error(e.message || i18n.t('Common:error'));

  const [createMutation, { loading: creating }] =
    useCreateContainerTypeGroupMutation({
      refetchQueries: REFETCH,
      awaitRefetchQueries: true,
      onError: onMutationError,
    });

  const [updateMutation, { loading: updating }] =
    useUpdateContainerTypeGroupMutation({
      refetchQueries: REFETCH,
      awaitRefetchQueries: true,
      onError: onMutationError,
    });

  const [deleteMutation, { loading: deleting }] =
    useDeleteContainerTypeGroupMutation({
      refetchQueries: REFETCH,
      awaitRefetchQueries: true,
      onError: onMutationError,
    });

  const groups: ContainerTypeGroup[] = data?.getAllContainerTypeGroups ?? [];

  /**
   * Create takes only a name; if a category was chosen, follow up with an
   * update (the colugo create mutation has no category argument).
   */
  const createGroup = async (
    name: string,
    category: GroupCategory,
  ): Promise<boolean> => {
    const res = await createMutation({ variables: { name: name.trim() } });
    const newId = res.data?.createContainerTypeGroup?.id;
    if (newId == null) return false;
    if (category != null) {
      const updated = await updateMutation({
        variables: { id: newId, payload: { category } },
      });
      if (!updated.data?.updateContainerTypeGroup) return false;
    }
    toast.success(i18n.t('EditContainerTypeGroups:toastCreated'));
    return true;
  };

  const saveGroup = async (
    id: number,
    payload: { name: string; category: GroupCategory },
  ): Promise<boolean> => {
    const res = await updateMutation({
      variables: {
        id,
        payload: { name: payload.name.trim(), category: payload.category },
      },
    });
    if (!res.data?.updateContainerTypeGroup) return false;
    toast.success(i18n.t('EditContainerTypeGroups:toastSaved'));
    return true;
  };

  const deleteGroup = async (id: number): Promise<boolean> => {
    const res = await deleteMutation({ variables: { id } });
    if (!res.data?.deleteContainerTypeGroup) return false;
    toast.success(i18n.t('EditContainerTypeGroups:toastDeleted'));
    return true;
  };

  return {
    groups,
    loading,
    error,
    creating,
    updating,
    deleting,
    createGroup,
    saveGroup,
    deleteGroup,
  };
}
